import type { ThemeConfig } from 'antd';

const theme: ThemeConfig = {
  token: {
    // ── Primary Colors (Evimo Design System) ──
    colorPrimary: '#1E6DF6', // Bleu Evimo — actions only
    colorLink: '#1E6DF6',
    colorSuccess: '#00a87e', // Teal — positive / completed
    colorWarning: '#ec7e00', // Orange — pending
    colorError: '#e23b4a', // Red — failed / danger

    // ── Typography ──
    fontFamily:
      "'Geist', var(--font-body), ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    fontSize: 13,
    fontSizeHeading1: 32, // Page Title (Aeonik Pro applied via CSS)
    fontSizeHeading2: 28, // KPI Value
    fontSizeHeading3: 18, // Section Header
    fontSizeHeading4: 14,
    fontSizeHeading5: 12,
    fontWeightStrong: 500,
    lineHeightHeading1: 1.2,
    lineHeightHeading2: 1.0,
    lineHeightHeading3: 1.4,
    colorTextHeading: '#191c1f',
    colorText: '#191c1f',
    colorTextSecondary: '#5f6563',
    colorTextTertiary: '#868e8b',

    // ── Layout & Surfaces ──
    colorBgBase: '#ffffff',
    colorBgLayout: '#f9f9fb', // App background
    colorBgContainer: '#ffffff', // Cards / panels
    colorBorderSecondary: '#e6e9e8', // v2 line
    colorBorder: '#d7dad9', // v2 line-strong (fields)
    colorFillAlter: '#f2f4f3', // v2 hover

    // ── Radius ──
    borderRadius: 7, // v2 inputs, selects, filter buttons
    borderRadiusLG: 16, // Cards
    borderRadiusSM: 4,

    // ── Shadows: NONE (flat design) ──
    boxShadow: 'none',
    boxShadowSecondary: 'none',
    boxShadowTertiary: 'none',
  },

  components: {
    // ── Card ──
    Card: {
      borderRadiusLG: 16,
      paddingLG: 24,
      boxShadow: 'none',
    },

    // ── Button (v2 rectangular, same as filters) ──
    Button: {
      borderRadius: 7,
      borderRadiusLG: 7,
      borderRadiusSM: 7,
      fontWeight: 550,
      controlHeight: 32,
    },

    // ── Tag / Badge (Pill shape) ──
    Tag: {
      borderRadiusSM: 9999,
      fontSizeSM: 12,
    },

    // ── Input / filters (v2 .input) ──
    Input: {
      borderRadius: 7,
      controlHeight: 32,
      paddingInline: 10,
      fontSize: 13.5,
      activeBorderColor: '#1E6DF6',
      hoverBorderColor: '#d7dad9',
      activeShadow: '0 0 0 3px #eef4ff',
    },

    Select: {
      borderRadius: 7,
      controlHeight: 32,
      fontSize: 13.5,
      optionSelectedBg: '#eef4ff',
      optionActiveBg: '#f2f4f3',
    },

    DatePicker: {
      borderRadius: 7,
      controlHeight: 32,
      fontSize: 13.5,
      activeBorderColor: '#1E6DF6',
      hoverBorderColor: '#d7dad9',
      activeShadow: '0 0 0 3px #eef4ff',
    },

    InputNumber: {
      borderRadius: 7,
      controlHeight: 32,
      fontSize: 13.5,
      activeBorderColor: '#1E6DF6',
      activeShadow: '0 0 0 3px #eef4ff',
    },

    Cascader: {
      borderRadius: 7,
      controlHeight: 32,
      fontSize: 13.5,
    },

    // ── Table ──
    Table: {
      headerBg: '#f4f4f4',
      headerColor: '#505a63',
      headerSplitColor: 'transparent',
      rowHoverBg: '#f9fafa',
      borderColor: '#e2e8f0',
      cellPaddingBlock: 16, // ~64px row height with content
      cellPaddingInline: 16,
      fontSize: 14,
    },

    // ── Menu (Sidebar) ──
    Menu: {
      itemBg: 'transparent',
      itemColor: '#505a63',
      itemHoverColor: '#191c1f',
      itemHoverBg: '#eef3f8',
      itemSelectedColor: '#191c1f',
      itemSelectedBg: 'rgba(30, 109, 246, 0.08)',
      subMenuItemBg: 'transparent',
      subMenuItemSelectedColor: '#191c1f',
      activeBarBorderWidth: 0,
      activeBarWidth: 0,
      groupTitleColor: '#7b8794',
    },

    // ── Layout ──
    Layout: {
      headerBg: '#ffffff',
      siderBg: '#f5f7fa',
      bodyBg: '#f9f9fb',
    },

    // ── Modal ──
    Modal: {
      borderRadiusLG: 16,
    },
  },
};

export const antdAppTheme = theme;

/** Stable ConfigProvider props — kept out of Providers.tsx to limit HMR blast radius. */
export const antdConfigProviderTheme = {
  token: antdAppTheme.token,
  components: antdAppTheme.components,
} as const;

export const antdConfigProviderModal = {
  styles: {
    header: {
      paddingInline: 32,
      paddingTop: 16,
      paddingBottom: 0,
    },
    body: {
      maxHeight: '70vh',
      overflowY: 'auto' as const,
      overflowX: 'hidden' as const,
      paddingInline: 32,
      paddingBlock: 16,
    },
    footer: {
      paddingInline: 32,
      paddingTop: 12,
      paddingBottom: 24,
    },
  },
  centered: true,
} as const;
